/**
 * Announcement Service
 * Handles API calls for announcement/message board management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export interface Announcement {
  announcementID: number;
  title: string;
  message: string;
  announcementType: 'Deadline' | 'Audit' | 'Visit' | 'Event' | 'General';
  priority: 'High' | 'Medium' | 'Low';
  startDate: string;
  endDate?: string;
  createdByStaffID: number;
  createdByName: string;
  targetRole: 'All' | 'Admin' | 'Supervisor' | 'Success Coach' | 'Viewer';
  isActive?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAnnouncementData {
  title: string;
  message: string;
  announcementType: 'Deadline' | 'Audit' | 'Visit' | 'Event' | 'General';
  priority?: 'High' | 'Medium' | 'Low';
  startDate?: string;
  endDate?: string;
  targetRole?: 'All' | 'Admin' | 'Supervisor' | 'Success Coach' | 'Viewer';
}

/**
 * Get active announcements for current user
 * Excludes dismissed announcements
 */
export async function getActiveAnnouncements(): Promise<Announcement[]> {
  const response = await apiGet<Announcement[]>('/announcements');

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch announcements');
  }

  return response.data || [];
}

/**
 * Get all announcements (admin only)
 */
export async function getAllAnnouncements(): Promise<Announcement[]> {
  const response = await apiGet<Announcement[]>('/announcements/all');

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch announcements');
  }

  return response.data || [];
}

/**
 * Create new announcement (admin only)
 */
export async function createAnnouncement(data: CreateAnnouncementData): Promise<Announcement> {
  const response = await apiPost<Announcement>('/announcements', data);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to create announcement');
  }

  return response.data;
}

/**
 * Update announcement (admin only)
 */
export async function updateAnnouncement(
  announcementID: number,
  data: Partial<CreateAnnouncementData>
): Promise<Announcement> {
  const response = await apiPut<Announcement>(`/announcements/${announcementID}`, data);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to update announcement');
  }

  return response.data;
}

/**
 * Delete announcement (soft delete, admin only)
 */
export async function deleteAnnouncement(announcementID: number): Promise<void> {
  const response = await apiDelete<void>(`/announcements/${announcementID}`);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete announcement');
  }
}

/**
 * Dismiss announcement for current user
 * Persists across devices/sessions
 */
export async function dismissAnnouncement(announcementID: number): Promise<void> {
  const response = await apiPost<void>(`/announcements/${announcementID}/dismiss`, {});

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to dismiss announcement');
  }
}

/**
 * Mark announcement as read (without dismissing)
 */
export async function markAnnouncementAsRead(announcementID: number): Promise<void> {
  const response = await apiPost<void>(`/announcements/${announcementID}/read`, {});

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to mark announcement as read');
  }
}
